import React, { useEffect, useState } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import './ConnectionBrizTv.scss';
import { GlobalRouteNames, LocalStorageItems, SuccessRouteNames } from '../../models/Common';
import { useAppDispatch } from '../../hooks/redux';
import { appSlice } from '../../store/reducers/AppSlice';
import { ForCtv } from '../../components/connectionBrizTvParts/ForCtv';
import { ForIPTV } from '../../components/connectionBrizTvParts/ForIPTV';
import { userInfoApi } from '../../services/apiServices/user/userInfo';
import { iptvApi } from '../../services/apiServices/user/iptv';
import { ordersApi } from '../../services/apiServices/user/orders';
import { useCustomSnackbar } from '../../hooks/useCustomEnqueueSnackbar';
import { brizTvShareId, suspensionOfServicesIds, isBrizTvFeatureEnabled } from '../../constants/configuration';
import { useFindShare } from '../../hooks/useFindShare';
import { getBrizTvCredentials, handleBrizTvShareRegistrationAnalytics, getErrorMessage } from '../../utils';

export const ConnectionBrizTv = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const { setSuccess } = appSlice.actions;
  const [isCtvOnly, setIsCtvOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistrationActivated, setIsRegistrationActivated] = useState(false);
  const { share, isUserInfoDownloaded } = useFindShare(brizTvShareId);
  const { data: connectedAccounts, isLoading: isLoadingConnectedAccounts } =
    userInfoApi.useGetConnectedAccountsQuery('');
  const controlledAccount = localStorage.getItem(LocalStorageItems.ControlledAccount);
  const { useGetUserInfoQuery, useGetUserInfoMultiAccQuery } = userInfoApi;
  const { data: userInfoState, refetch: userInfoRefetch } = controlledAccount
    ? useGetUserInfoMultiAccQuery({ subAccountId: JSON.parse(controlledAccount).UserID })
    : useGetUserInfoQuery('');

  const [connectBrizTvApi] = iptvApi.useConnectBrizTvMutation();
  const [activateShareApi] = iptvApi.useActivationShareMutation();
  const [callback, { isLoading: isLoadingCallback }] = ordersApi.useAuthorizedCallbackMutation();
  const iptvUnion = connectedAccounts?.union_accounts?.find(
    (account) => !!account.Iptv && !account.Service?.Daily && !suspensionOfServicesIds.includes(account.Service?.id),
  );
  const iptvUnionId = iptvUnion?.Iptv?.id;
  const isIptvUnionHasCredentials = iptvUnion?.Iptv?.BrizTV !== null;
  const accountIdIptv = iptvUnionId || 0;
  const unionIptvBrizTVCredentials = getBrizTvCredentials(connectedAccounts?.union_accounts);
  const isBrizTVAccountExist = unionIptvBrizTVCredentials?.Password || false;
  const isBrizTvShareAvailable = userInfoState?.Permissions.Services.Iptv.BrizTVAvailable;
  const connectedAccountsList = Object.keys(connectedAccounts || {})
    .map((key) => connectedAccounts?.[key] || [])
    .flat();
  const areAvailableBrizTvCards =
    connectedAccountsList.filter((el) => el.Service?.Daily || suspensionOfServicesIds.includes(el.Service?.id)).length <
      (connectedAccounts?.union_accounts?.filter((el) => !!el.Iptv) || []).length &&
    (connectedAccounts?.union_accounts?.filter((el) => !!el.Iptv) || []).length > 0;
  const isServiceExAvailable = !!(
    connectedAccounts?.union_accounts?.filter((el) => el?.Iptv?.BrizTV !== null && el?.Iptv?.ServicesEx?.length)[0]
      ?.Iptv?.BrizTV || null
  );

  useEffect(() => {
    const isBrizTvAvailable =
      isBrizTvFeatureEnabled && !share && isBrizTvShareAvailable && areAvailableBrizTvCards && !isServiceExAvailable;
    setIsCtvOnly(!isBrizTvAvailable);
  }, [share, isBrizTvShareAvailable, areAvailableBrizTvCards, isServiceExAvailable, isUserInfoDownloaded]);

  const handleCallMeBack = async ({ phone }: { phone: string }) => {
    const comment = 'Заявка на подключение к BRIZ.TV';
    const commentMessage = `${comment}. Тел: ${phone}`;
    try {
      await callback({ phone, comment: commentMessage })
        .unwrap()
        .then(() => {
          dispatch(setSuccess(true));
          history.push(`${GlobalRouteNames.SuccessPage}${SuccessRouteNames.OttSuccessfulRequestPage}`);
        });
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
    }
  };

  const activateShare = async (accountId: string, shareId: string) => {
    try {
      await activateShareApi({ shareId, accountId })
        .unwrap()
        .then(() => {
          setTimeout(() => {
            setIsLoading(false);
            dispatch(setSuccess(true));
            userInfoRefetch();
            handleBrizTvShareRegistrationAnalytics();
            history.push(`${GlobalRouteNames.SuccessPage}${SuccessRouteNames.BrizTvPromotion}`);
          }, 3000);
        });
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
      setIsLoading(false);
    }
  };

  const handleRegisterBrizTv = async ({
    password,
    phone,
    email,
  }: {
    password: string;
    phone: string;
    email: string;
  }) => {
    setIsLoading(true);
    setIsRegistrationActivated(true);

    try {
      await connectBrizTvApi({
        password,
        accountId: accountIdIptv,
        ...(!!email && { email }),
        ...(!!phone && { phone }),
      })
        .unwrap()
        .then(() => {
          if (phone) {
            localStorage.setItem(LocalStorageItems.BrizTvPhone, phone);
          } else {
            localStorage.removeItem(LocalStorageItems.BrizTvPhone);
          }
          if (email) {
            localStorage.setItem(LocalStorageItems.BrizTvEmail, email);
          } else {
            localStorage.removeItem(LocalStorageItems.BrizTvEmail);
          }
          localStorage.setItem(LocalStorageItems.BrizTvPassword, password);
          activateShare(`${accountIdIptv}`, brizTvShareId || '');
        });
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
      setIsLoading(false);
      setIsRegistrationActivated(false);
    }
  };

  const activateShareOneClick = async () => {
    setIsLoading(true);
    try {
      await activateShare(`${accountIdIptv}`, brizTvShareId || '');
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
      setIsLoading(false);
    }
  };

  const shareConnectRoute = () => {
    return (
      <Route path={GlobalRouteNames.ConnectionBrizTv} exact>
        <div className="connection-briz-tv page">
          <div className="connection-briz-tv__container container">
            <div className="connection-briz-tv__card card card__primary card__primary_full-mobile">
              {isCtvOnly ? (
                <ForCtv handleCallMeBack={handleCallMeBack} isLoadingCallMeBack={isLoadingCallback} />
              ) : (
                <ForIPTV
                  handleConnectBrizTv={handleRegisterBrizTv}
                  isLoadingConnectBrizTv={isLoading}
                  isBrizTvExist={isIptvUnionHasCredentials}
                  handleActivateShareOneClick={activateShareOneClick}
                  isRegistrationActivated={isRegistrationActivated}
                />
              )}
            </div>
          </div>
        </div>
      </Route>
    );
  };

  if (!isUserInfoDownloaded || isLoadingConnectedAccounts) {
    return <div className="connection-briz-tv page skeleton" />;
  }

  return (
    <Switch>
      {/* eslint-disable-next-line no-nested-ternary */}
      {isBrizTVAccountExist && isUserInfoDownloaded && !isLoadingConnectedAccounts ? (
        share ? (
          <Redirect to={`${GlobalRouteNames.SuccessPage}${SuccessRouteNames.BrizTvConnected}`} />
        ) : (
          shareConnectRoute()
        )
      ) : (
        shareConnectRoute()
      )}
    </Switch>
  );
};
